import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { MuiTelInput } from 'mui-tel-input';
import {
  addClubPortalContact,
  addManagedClubContact,
  addSecretaryPortalContact,
  getClubPortalContacts,
  getManagedClubContacts,
  getSecretaryPortalContacts,
  portalErrorMessage,
  removeClubPortalContact,
  removeManagedClubContact,
  removeSecretaryPortalContact
} from '../../features/portal/portalService';

const EMPTY_FORM = { name: '', email: '', phone: '' };

const PortalContactsDialog = ({
  open,
  onClose,
  mode,
  token,
  eventId,
  kind,
  venueId,
  venueName,
  workspaceScope,
  contactLimit = 20,
  onChanged
}) => {
  const isPortal = mode === 'portal';
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(() => {
    if (!open) return undefined;

    let cancelled = false;
    setLoading(true);
    setLoadError('');

    let request;
    if (isPortal) {
      request = getManagedClubContacts(token, venueId);
    } else if (kind === 'secretary') {
      request = getSecretaryPortalContacts(eventId);
    } else {
      request = getClubPortalContacts(eventId, venueId);
    }

    request
      .then((response) => {
        if (!cancelled) setContacts(response?.contacts || []);
      })
      .catch((requestError) => {
        if (!cancelled) {
          setLoadError(
            portalErrorMessage(
              requestError,
              'The contacts could not be loaded. Please try again.'
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isPortal, token, venueId, kind, eventId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setActionError('');
    setDeleteTarget(null);
  }, [open]);

  const atLimit = contacts.length >= contactLimit;

  const updateForm = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleAdd = async (event) => {
    event.preventDefault();
    if (saving || atLimit) return;

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim()
    };

    if (!payload.name || !payload.email) {
      setActionError('Enter a name and email address.');
      return;
    }

    setSaving(true);
    setActionError('');

    try {
      let result;
      if (isPortal) {
        result = await addManagedClubContact(token, venueId, payload);
      } else if (kind === 'secretary') {
        result = await addSecretaryPortalContact(eventId, payload);
      } else {
        result = await addClubPortalContact(eventId, venueId, payload);
      }

      setContacts(result?.contacts || []);
      setForm(EMPTY_FORM);
      onChanged?.();
    } catch (requestError) {
      setActionError(
        portalErrorMessage(
          requestError,
          'The contact could not be added. Please try again.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!deleteTarget || removing) return;

    setRemoving(true);
    setActionError('');

    try {
      let result;
      if (isPortal) {
        result = await removeManagedClubContact(token, venueId, deleteTarget.id);
      } else if (kind === 'secretary') {
        result = await removeSecretaryPortalContact(eventId, deleteTarget.id);
      } else {
        result = await removeClubPortalContact(eventId, venueId, deleteTarget.id);
      }

      setContacts(result?.contacts || []);
      setDeleteTarget(null);
      onChanged?.();
    } catch (requestError) {
      setActionError(
        portalErrorMessage(
          requestError,
          'The contact could not be removed. Please try again.'
        )
      );
      setDeleteTarget(null);
    } finally {
      setRemoving(false);
    }
  };

  const busy = saving || removing;
  const label = venueName || 'this portal';
  let scopeCopy = 'These contacts are shared by every event at this club in this workspace.';
  if (kind === 'secretary') {
    scopeCopy = workspaceScope === 'none'
      ? 'These contacts are shared by every event that is not in a workspace.'
      : 'These contacts are shared by every event in this workspace.';
  } else if (workspaceScope === 'none') {
    scopeCopy =
      'These contacts are shared by every event at this club that is not in a workspace.';
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="portal-contacts-title">
        <DialogTitle id="portal-contacts-title">
          Contacts for {label}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Add the people who should receive the portal link and PIN. Each contact gets a
            personal PIN when you send them the link from Link &amp; QR.
          </Typography>
          <Alert severity={workspaceScope === 'none' ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {scopeCopy}
          </Alert>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            mb={1}>
            <Typography variant="subtitle2">
              {contacts.length} of {contactLimit} contacts
            </Typography>
            {atLimit && (
              <Typography variant="caption" color="text.secondary">
                Remove a contact before adding another.
              </Typography>
            )}
          </Stack>

          {loading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && loadError && (
            <Box mb={2}>
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={load}>
                    Retry
                  </Button>
                }>
                {loadError}
              </Alert>
            </Box>
          )}

          {!loading && !loadError && contacts.length === 0 && (
            <Typography variant="body2" color="text.secondary" py={2}>
              No contacts yet. Add the first person below.
            </Typography>
          )}

          {!loading && !loadError && contacts.length > 0 && (
            <List dense disablePadding sx={{ mb: 1 }}>
              {contacts.map((contact) => (
                <ListItem
                  key={contact.id}
                  disableGutters
                  secondaryAction={
                    <Tooltip title="Remove contact">
                      <IconButton
                        edge="end"
                        aria-label={`Remove ${contact.name || contact.email}`}
                        onClick={() => {
                          setActionError('');
                          setDeleteTarget(contact);
                        }}
                        disabled={busy}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  }>
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap>
                        <Typography variant="body2" fontWeight={600}>
                          {contact.name || 'Contact'}
                        </Typography>
                        {contact.legacy && (
                          <Chip size="small" variant="outlined" label="Saved email" />
                        )}
                        {!contact.legacy && contact.personal?.hasPin && (
                          <Chip
                            size="small"
                            color={contact.personal.revoked ? 'default' : 'success'}
                            variant="outlined"
                            label={
                              contact.personal.revoked ? 'PIN revoked' : 'Personal PIN'
                            }
                          />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Box component="span" display="block">
                        <Typography
                          variant="body2"
                          component="span"
                          color="text.secondary"
                          display="block">
                          {contact.email}
                        </Typography>
                        {contact.phone && (
                          <Stack
                            component="span"
                            direction="row"
                            spacing={0.5}
                            alignItems="center">
                            <WhatsAppIcon sx={{ fontSize: 14, color: 'success.main' }} />
                            <Typography
                              variant="caption"
                              component="span"
                              color="text.secondary">
                              {contact.phone}
                            </Typography>
                          </Stack>
                        )}
                        {!contact.legacy && contact.personal?.lastLoginAt && (
                          <Typography
                            variant="caption"
                            component="span"
                            color="text.secondary"
                            display="block">
                            Last signed in{' '}
                            {new Date(contact.personal.lastLoginAt).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {actionError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setActionError('')}>
              {actionError}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Box component="form" onSubmit={handleAdd}>
            <Typography variant="subtitle2" gutterBottom>
              Add a contact
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Phone numbers are used for WhatsApp sharing.
            </Typography>
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="Name"
                  value={form.name}
                  onChange={updateForm('name')}
                  size="small"
                  fullWidth
                  required
                  disabled={busy || atLimit}
                  inputProps={{ maxLength: 100 }}
                />
                <TextField
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={updateForm('email')}
                  size="small"
                  fullWidth
                  required
                  disabled={busy || atLimit}
                  inputProps={{ maxLength: 254 }}
                />
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                <MuiTelInput
                  label="Phone (optional)"
                  value={form.phone}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, phone: value }))
                  }
                  defaultCountry="za"
                  size="small"
                  fullWidth
                  disabled={busy || atLimit}
                />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PersonAddIcon />
                    )
                  }
                  disabled={busy || atLimit}
                  sx={{ whiteSpace: 'nowrap', minHeight: 40 }}>
                  Add
                </Button>
              </Stack>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={removing ? undefined : () => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Remove this contact?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget?.name || deleteTarget?.email || 'This contact'} will no longer
            receive portal emails for {label}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={removing}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRemove}
            disabled={removing}>
            {removing ? <CircularProgress size={18} color="inherit" /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PortalContactsDialog;
